import NoteBody from '@/components/NoteBody';
import type { AffectUser } from '@/types/affectUser';
import { Typography } from '@/ui';
import type { EndingCustomExpectation } from '@/utils/groupEndingCustomExpectations';

import './styles.css';
import type { Props } from './types';

const userName = (user?: AffectUser) =>
  user?.display_name ||
  [user?.first_name, user?.last_name].filter(Boolean).join(' ') ||
  user?.email ||
  null;

const addedBy = ({ createdBy, createdAt }: EndingCustomExpectation) => {
  const name = userName(createdBy);
  const date = new Date(createdAt).toLocaleDateString([], { dateStyle: 'medium' });
  return `Added ${name ? `by ${name} ` : ''}on ${date}`;
};

const EndingCustomExpectations = ({ items }: Props) => {
  if (items.length === 0) return null;

  return (
    <section>
      <Typography variant="subheading" className="mb-1">
        Custom expectations that will end
      </Typography>
      <Typography muted className="mb-2">
        Custom expectations are extra or adjusted appointments added for this member on top of their
        program&apos;s schedule. They won&apos;t carry over to the new phase, so re-add any that
        still apply.
      </Typography>
      <ul className="flex flex-col gap-2 text-sm" data-testid="ending-custom-expectations">
        {items.map((item) => (
          // Red because these are removed along with the current phase.
          <li
            key={item.id}
            className="rounded border-l-4 border-red-500 bg-red-50 px-3 py-2 dark:bg-red-950">
            <div>
              <Typography as="span" weight="medium" className="text-red-800 dark:text-red-300">
                {item.title}
              </Typography>
              <Typography as="span" muted>{` · ${item.detail}`}</Typography>
            </div>
            <div className="mt-1">
              {item.note ? (
                <NoteBody note={item.note} />
              ) : (
                <Typography as="span" muted className="italic">
                  No note recorded for why this was added.
                </Typography>
              )}
            </div>
            <Typography variant="caption" as="div" muted className="mt-1">
              {addedBy(item)}
            </Typography>
          </li>
        ))}
      </ul>
    </section>
  );
};

export default EndingCustomExpectations;
